// ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getLoggedInUserProfile, 
  updateUserProfile, 
  changePassword,
  uploadUserAvatar,
  removeUserAvatar
} from '../../services/authService';
import Loader from '../../components/common/Loader';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { ArrowRightOnRectangleIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';

function ProfilePage() {
  const { user, isLoading: authLoading, logout } = useAuth();
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  
  // Validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username);
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s]{2,30}$/;
    return nameRegex.test(name);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar
    };
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'username':
        if (!value) {
          error = 'Username is required';
        } else if (value.length < 3) {
          error = 'Username must be at least 3 characters';
        } else if (value.length > 20) {
          error = 'Username must be less than 20 characters';
        } else if (!validateUsername(value)) {
          error = 'Username can only contain letters, numbers, and underscores';
        }
        break;
        
      case 'email':
        if (!value) {
          error = 'Email is required';
        } else if (!validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
        
      case 'first_name':
        if (!value) {
          error = 'First name is required';
        } else if (value.length < 2) {
          error = 'First name must be at least 2 characters';
        } else if (!validateName(value)) {
          error = 'First name can only contain letters and spaces';
        }
        break;
        
      case 'last_name':
        if (!value) {
          error = 'Last name is required';
        } else if (value.length < 2) {
          error = 'Last name must be at least 2 characters';
        } else if (!validateName(value)) {
          error = 'Last name can only contain letters and spaces';
        }
        break;
        
      case 'phone':
        if (value && !validatePhone(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  const validatePasswordField = (name, value, otherValues = {}) => {
    let error = '';
    
    switch (name) {
      case 'currentPassword':
        if (!value) {
          error = 'Current password is required';
        }
        break;
        
      case 'newPassword':
        if (!value) {
          error = 'New password is required';
        } else {
          const validation = validatePassword(value);
          if (!validation.isValid) {
            error = 'Password must meet all security requirements';
          }
        }
        break;
        
      case 'confirmPassword':
        if (!value) {
          error = 'Please confirm your new password';
        } else if (value !== otherValues.newPassword) {
          error = 'Passwords do not match';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      console.log('ProfilePage useEffect - user:', user);
      console.log('ProfilePage useEffect - authLoading:', authLoading);
      
      if (user && (user.id || user.username)) {
        console.log('Fetching profile for user:', user.id || user.username);
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
          const fetchedProfile = await getLoggedInUserProfile();
          console.log('Fetched profile response:', fetchedProfile);
          
          // The backend returns { profile: {...}, login_history: [...], activity_stats: {...} }
          if (fetchedProfile.profile) {
            setProfileData({
              username: fetchedProfile.profile.username || '',
              email: fetchedProfile.profile.email || '',
              first_name: fetchedProfile.profile.first_name || '',
              last_name: fetchedProfile.profile.last_name || '',
              phone: fetchedProfile.profile.phone || '',
            });
          } else {
            console.warn('No profile data in response:', fetchedProfile);
          }
          
          // Set profile stats for display
          setProfileStats({
            loginHistory: fetchedProfile.login_history || [],
            activityStats: fetchedProfile.activity_stats || {}
          });
          
        } catch (err) {
          setError('Failed to load profile data: ' + err.message);
          console.error('Profile fetch error:', err);
          
          // Fallback: use data from auth context if available
          if (user) {
            console.log('Using fallback user data from auth context:', user);
            setProfileData({
              username: user.username || '',
              email: user.email || '',
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              phone: user.phone || '',
            });
          }
        } finally {
          setLoading(false);
        }
      } else if (!authLoading && !user) {
        console.log('No user found, showing auth error');
        setLoading(false);
        setError('Authentication required. Please log in.');
      } else {
        console.log('Waiting for auth or user data...');
      }
    };

    fetchProfile();
  }, [user, authLoading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    
    // Real-time validation
    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    // Check if form is valid
    const newErrors = { ...validationErrors, [name]: error };
    const hasErrors = Object.values(newErrors).some(err => err !== '');
    setIsFormValid(!hasErrors);
    
    setError(''); // Clear general errors when typing
    setSuccessMessage('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    const newPasswordData = { ...passwordData, [name]: value };
    setPasswordData(newPasswordData);
    
    // Real-time password validation
    const error = validatePasswordField(name, value, newPasswordData);
    setPasswordValidation(prev => ({
      ...prev,
      [name]: error
    }));
    
    // Special handling for new password strength indicator
    if (name === 'newPassword') {
      setPasswordValidation(prev => ({
        ...prev,
        strength: validatePassword(value)
      }));
    }
    
    setPasswordError(''); // Clear password errors when typing
  };

  // Validation message component
  const ValidationMessage = ({ error, type = 'error' }) => {
    if (!error) return null;
    
    const styles = {
      error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-700',
      warning: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700',
      success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
    };
    
    const icons = {
      error: '❌',
      warning: '⚠️',
      success: '✅'
    };
    
    return (
      <div className={`${styles[type]} border px-3 py-2 rounded-lg text-sm font-medium mt-1 shadow-sm animate-fade-in`}>
        <div className="flex items-center">
          <span className="mr-2">{icons[type]}</span>
          {error}
        </div>
      </div>
    );
  };

  // Password strength component
  const PasswordStrength = ({ strength }) => {
    if (!strength) return null;
    
    const requirements = [
      { key: 'minLength', label: 'At least 8 characters', met: strength.minLength },
      { key: 'hasUpperCase', label: 'One uppercase letter', met: strength.hasUpperCase },
      { key: 'hasLowerCase', label: 'One lowercase letter', met: strength.hasLowerCase },
      { key: 'hasNumbers', label: 'One number', met: strength.hasNumbers },
      { key: 'hasSpecialChar', label: 'One special character', met: strength.hasSpecialChar }
    ];
    
    const metCount = requirements.filter(req => req.met).length;
    const strengthLevel = metCount <= 2 ? 'weak' : metCount <= 4 ? 'medium' : 'strong';
    const strengthColors = {
      weak: 'bg-red-500',
      medium: 'bg-amber-500', 
      strong: 'bg-green-500'
    };
    
    return (
      <div className="mt-2 p-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Password Strength</span>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`h-2 w-4 rounded-full ${
                  level <= metCount ? strengthColors[strengthLevel] : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
        <div className="space-y-1">
          {requirements.map((req) => (
            <div key={req.key} className="flex items-center text-xs">
              <span className={`mr-2 ${req.met ? 'text-green-500' : 'text-gray-400'}`}>
                {req.met ? '✓' : '○'}
              </span>
              <span className={req.met ? 'text-green-700' : 'text-gray-500'}>
                {req.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    
    try {
      // Prepare profile data for update (only send changed fields with valid values)
      const updateData = {};
      
      // Only add fields that have values and are different from current user data
      if (profileData.username && profileData.username.trim() && profileData.username !== user.username) {
        updateData.username = profileData.username.trim();
      }
      if (profileData.email && profileData.email.trim() && profileData.email !== user.email) {
        updateData.email = profileData.email.trim();
      }
      if (profileData.first_name && profileData.first_name.trim() && profileData.first_name !== user.first_name) {
        updateData.first_name = profileData.first_name.trim();
      }
      if (profileData.last_name && profileData.last_name.trim() && profileData.last_name !== user.last_name) {
        updateData.last_name = profileData.last_name.trim();
      }
      if (profileData.phone && profileData.phone.trim() && profileData.phone !== user.phone) {
        updateData.phone = profileData.phone.trim();
      }
      
      console.log('Update data being sent:', updateData);
      
      if (Object.keys(updateData).length === 0) {
        setSuccessMessage('No changes to save');
        setIsEditing(false);
        return;
      }
      
      await updateUserProfile(updateData);
      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
      
      // Refresh profile data
      const fetchedProfile = await getLoggedInUserProfile();
      if (fetchedProfile.profile) {
        setProfileData({
          username: fetchedProfile.profile.username || '',
          email: fetchedProfile.profile.email || '',
          first_name: fetchedProfile.profile.first_name || '',
          last_name: fetchedProfile.profile.last_name || '',
          phone: fetchedProfile.profile.phone || '',
        });
      }
      
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
      console.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;
    
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      await uploadUserAvatar(avatarFile);
      setSuccessMessage('Avatar updated successfully!');
      setAvatarFile(null);
      setAvatarPreview(null);
      
      // Refresh profile data to get new avatar URL
      const fetchedProfile = await getLoggedInUserProfile();
      if (fetchedProfile.profile) {
        setProfileData(prev => ({
          ...prev,
          avatar_url: fetchedProfile.profile.avatar_url || ''
        }));
      }
      
    } catch (err) {
      setError('Failed to upload avatar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      await removeUserAvatar();
      setSuccessMessage('Avatar removed successfully!');
      setProfileData(prev => ({ ...prev, avatar_url: '' }));
      
    } catch (err) {
      setError('Failed to remove avatar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setSuccessMessage('');
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    
    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    setLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccessMessage('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
    } catch (err) {
      setPasswordError('Failed to change password: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return <Loader />;
  }

  if (error && !user) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative m-6 shadow-md" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
          My Profile
        </h2>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl relative mb-6 shadow-lg mx-2" role="alert">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              <span className="block sm:inline font-medium">{successMessage}</span>
            </div>
          </div>
        )}

      {/* Avatar Section - DISABLED: Backend doesn't support avatar_url column */}
      {false && (
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
        <h3 className="text-2xl font-semibold text-gray-800 mb-6">Profile Picture</h3>
        
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {(avatarPreview || profileData.avatar_url) ? (
                <img 
                  src={avatarPreview || profileData.avatar_url} 
                  alt="Profile Avatar" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <PhotoIcon className="w-12 h-12 text-gray-400" />
              )}
            </div>
          </div>
          
          <div className="flex-1">
            <div className="flex flex-wrap gap-3">
              <label className="cursor-pointer">
                <Button type="button" variant="secondary" className="inline-flex items-center">
                  <PhotoIcon className="w-4 h-4 mr-2" />
                  Choose Photo
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </label>
              
              {avatarFile && (
                <Button 
                  type="button" 
                  onClick={handleUploadAvatar}
                  disabled={loading}
                  variant="success"
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </Button>
              )}
              
              {profileData.avatar_url && (
                <Button 
                  type="button" 
                  onClick={handleRemoveAvatar}
                  disabled={loading}
                  variant="danger"
                  className="inline-flex items-center"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              JPG, PNG or WebP. Max size 5MB.
            </p>
          </div>
        </div>
      </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            Personal Information
          </h3>
        </div>
        
        {error && user && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl relative mb-6 shadow-lg" role="alert">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
              <span className="block sm:inline font-medium">{error}</span>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSaveProfile} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input 
                label="Username" 
                id="username" 
                name="username" 
                value={profileData.username} 
                onChange={handleInputChange} 
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-50' : validationErrors.username ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {isEditing && <ValidationMessage error={validationErrors.username} />}
            </div>
            
            <div>
              <Input 
                label="Email" 
                id="email" 
                name="email" 
                type="email" 
                value={profileData.email} 
                onChange={handleInputChange} 
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-50' : validationErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {isEditing && <ValidationMessage error={validationErrors.email} />}
            </div>
            
            <div>
              <Input 
                label="First Name" 
                id="first_name" 
                name="first_name" 
                value={profileData.first_name} 
                onChange={handleInputChange} 
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-50' : validationErrors.first_name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {isEditing && <ValidationMessage error={validationErrors.first_name} />}
            </div>
            
            <div>
              <Input 
                label="Last Name" 
                id="last_name" 
                name="last_name" 
                value={profileData.last_name} 
                onChange={handleInputChange} 
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-50' : validationErrors.last_name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {isEditing && <ValidationMessage error={validationErrors.last_name} />}
            </div>
            
            <div className="md:col-span-2">
              <Input 
                label="Phone" 
                id="phone" 
                name="phone" 
                value={profileData.phone} 
                onChange={handleInputChange} 
                disabled={!isEditing} 
                placeholder="Enter phone number (optional)"
                className={`${!isEditing ? 'bg-gray-50' : validationErrors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {isEditing && <ValidationMessage error={validationErrors.phone} />}
              {isEditing && !validationErrors.phone && profileData.phone && (
                <ValidationMessage error="Valid phone number format" type="success" />
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 bg-gray-50 p-6 rounded-xl">
            {isEditing ? (
              <>
                <Button 
                  type="button" 
                  onClick={() => {
                    setIsEditing(false);
                    setError('');
                    setSuccessMessage('');
                    setValidationErrors({});
                  }} 
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || Object.values(validationErrors).some(err => err !== '')}
                  className={`bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white ${
                    Object.values(validationErrors).some(err => err !== '') 
                      ? 'opacity-50 cursor-not-allowed' 
                      : ''
                  }`}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button 
                type="button" 
                onClick={() => {
                  setIsEditing(true);
                  // Validate all fields when starting to edit
                  const errors = {};
                  Object.keys(profileData).forEach(key => {
                    const error = validateField(key, profileData[key]);
                    if (error) errors[key] = error;
                  });
                  setValidationErrors(errors);
                }} 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                Edit Profile
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Password Change Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              Change Password
            </h3>
            <button
              type="button"
              onClick={() => {
                setIsChangingPassword(!isChangingPassword);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setPasswordError('');
              }}
              className="text-sm font-medium text-green-600 hover:text-green-500 bg-white px-4 py-2 rounded-lg border border-green-200 hover:bg-green-50 transition-colors"
            >
              {isChangingPassword ? 'Cancel' : 'Change Password'}
            </button>
          </div>

          {isChangingPassword && (
            <form onSubmit={handleChangePassword} className="mt-6 space-y-4">
              {passwordError && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-lg">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    <span className="font-medium">{passwordError}</span>
                  </div>
                </div>
              )}
              
              <div>
                <Input 
                  label="Current Password" 
                  id="currentPassword" 
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className={`${passwordValidation.currentPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
                />
                <ValidationMessage error={passwordValidation.currentPassword} />
              </div>
              
              <div>
                <Input 
                  label="New Password" 
                  id="newPassword" 
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  minLength="8"
                  required
                  className={`${passwordValidation.newPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
                />
                <ValidationMessage error={passwordValidation.newPassword} />
                {passwordData.newPassword && <PasswordStrength strength={passwordValidation.strength} />}
              </div>
              
              <div>
                <Input 
                  label="Confirm New Password" 
                  id="confirmPassword" 
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className={`${passwordValidation.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
                />
                <ValidationMessage error={passwordValidation.confirmPassword} />
                {passwordData.confirmPassword && !passwordValidation.confirmPassword && passwordData.confirmPassword === passwordData.newPassword && (
                  <ValidationMessage error="Passwords match!" type="success" />
                )}
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 bg-gray-50 p-6 rounded-xl">
                <Button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                    setPasswordValidation({});
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || Object.values(passwordValidation).some(err => typeof err === 'string' && err !== '') || !passwordValidation.strength?.isValid}
                  className={`bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white ${
                    (Object.values(passwordValidation).some(err => typeof err === 'string' && err !== '') || !passwordValidation.strength?.isValid)
                      ? 'opacity-50 cursor-not-allowed' 
                      : ''
                  }`}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Account Actions Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                Account Actions
              </h3>
              <p className="mt-2 text-gray-600 text-sm">Manage your account settings</p>
            </div>
            <Button
              type="button"
              onClick={logout}
              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white inline-flex items-center"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Profile Statistics */}
      {profileStats && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
            {/* Recent Login History */}
            {profileStats.loginHistory && profileStats.loginHistory.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
                  Recent Login History
                </h4>
                <div className="space-y-3">
                  {profileStats.loginHistory.slice(0, 5).map((login, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm border border-amber-200 hover:bg-amber-50 transition-colors">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                            {new Date(login.login_time).toLocaleDateString()} at {new Date(login.login_time).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1 ml-4">
                          IP: {login.ip_address}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 max-w-xs truncate">
                        {login.user_agent ? login.user_agent.substring(0, 50) + '...' : 'Unknown device'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default ProfilePage;